import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { ArrowUpRight, Database, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

const fmtMoney = (v) => (v == null ? "—" : `$${Number(v).toLocaleString()}`);

function RiskBadge({ level }) {
  if (!level) return <span className="overline text-gray-300">unanalyzed</span>;
  return <span className={`risk-pill ${level.toLowerCase()}`}>{level}</span>;
}

export default function CommercialView() {
  const { user } = useAuth();
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchDeals = async () => {
    const { data } = await api.get("/deals");
    setDeals(data);
    setLoading(false);
  };

  const seed = async () => {
    try {
      const { data } = await api.post("/deals/seed");
      toast.success(`${data.seeded} demo deals loaded`);
      fetchDeals();
    } catch {
      toast.error("Seeding failed");
    }
  };

  useEffect(() => {
    fetchDeals();
  }, []);

  return (
    <div className="max-w-[1400px] mx-auto px-6 py-8" data-testid="commercial-view">
      <div className="flex items-end justify-between mb-8">
        <div>
          <div className="overline text-gray-400 mb-2">Simulated CRM · HubSpot & Salesforce</div>
          <h1 className="font-heading text-4xl sm:text-5xl font-bold tracking-tight">
            Pipeline
          </h1>
          <p className="text-sm text-gray-500 mt-2 max-w-lg">
            iSale runs silently as you open each deal. Alerts only fire when reality disagrees with perception.
          </p>
        </div>
        {user?.role === "commercial" && (
          <Button
            onClick={seed}
            variant="outline"
            className="rounded-sm gap-2"
            data-testid="seed-deals-btn"
          >
            <Database className="w-3.5 h-3.5" />
            Load demo deals
          </Button>
        )}
      </div>

      {loading ? (
        <div className="text-sm text-gray-400">Loading...</div>
      ) : deals.length === 0 ? (
        <div className="border border-dashed border-gray-300 rounded-sm p-12 text-center">
          <Sparkles className="w-6 h-6 mx-auto text-gray-300 mb-3" />
          <div className="font-heading text-lg mb-1">No deals in view.</div>
          <p className="text-sm text-gray-500 mb-4">
            Seed the simulated CRM to see the Contradiction Engine in action.
          </p>
          <Button onClick={seed} className="rounded-sm bg-black hover:bg-gray-800" data-testid="seed-empty-btn">
            Load demo deals
          </Button>
        </div>
      ) : (
        <div className="border border-gray-200 bg-white rounded-sm overflow-hidden">
          <table className="w-full text-sm" data-testid="deals-table">
            <thead className="bg-gray-50/60 border-b border-gray-200">
              <tr className="text-left">
                <th className="py-3 px-4 overline text-gray-400">Deal</th>
                <th className="py-3 px-4 overline text-gray-400">Stage</th>
                <th className="py-3 px-4 overline text-gray-400 text-right">Value</th>
                <th className="py-3 px-4 overline text-gray-400 text-right">Reality</th>
                <th className="py-3 px-4 overline text-gray-400">Risk</th>
                <th className="py-3 px-4"></th>
              </tr>
            </thead>
            <tbody>
              {deals.map((d) => (
                <tr
                  key={d.id}
                  className="border-b border-gray-100 hover:bg-gray-50/60 transition-colors"
                  data-testid={`deal-row-${d.crm_id}`}
                >
                  <td className="py-3 px-4">
                    <div className="font-medium">{d.deal_name}</div>
                    <div className="text-xs text-gray-500">
                      <span className="font-mono">{d.crm_id}</span> · {d.company}
                    </div>
                  </td>
                  <td className="py-3 px-4 text-gray-700">{d.stage}</td>
                  <td className="py-3 px-4 text-right metric-value">{fmtMoney(d.deal_value)}</td>
                  <td className="py-3 px-4 text-right metric-value">
                    {d.latest_analysis?.reality_score?.toFixed(1) ?? "—"}
                  </td>
                  <td className="py-3 px-4">
                    <RiskBadge level={d.latest_analysis?.risk_level} />
                  </td>
                  <td className="py-3 px-4 text-right">
                    <Link
                      to={`/app/deals/${d.id}`}
                      className="inline-flex items-center gap-1 text-xs text-gray-900 hover:underline"
                      data-testid={`open-deal-${d.crm_id}`}
                    >
                      Open <ArrowUpRight className="w-3 h-3" />
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
